import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  Slider,
  IconButton,
  Button,
  Link,
  Tooltip,
  SelectChangeEvent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  LocalHospital as HospitalIcon,
  Assessment as MetricsIcon,
  Star as CareScoreIcon,
  Group as CohortsIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Timeline as TimelineIcon,
  Analytics as AnalyticsIcon,
  Route as RouteIcon,
  Person as ProviderIcon
} from '@mui/icons-material';

// Facility types from the requirements
const facilityTypes = [
  'Urgent Care/Walk-in Clinic',
  'Outpatient Clinic',
  'Ambulatory Surgical Center',
  'General Hospital',
  'Specialty Hospital',
  'Pathology/Testing Laboratory',
  'Clinical Laboratory',
  'General Pharmacy',
  'Specialty Pharmacy',
  'Birthing Center',
  'Community Health Center',
  'End-Stage Renal Disease Center',
  'Vaccination Clinic',
  'Assisted Living Facility',
  'Nursing Home',
  'Hospice',
  'Outpatient Rehabilitation Facility',
  'Behavioural Health Center',
  'Rural Clinic',
  'Psychiatric Treatment Center',
  'Other'
];

// Add specialties/subspecialties
const specialties = [
  'Cardiology',
  'Orthopedics',
  'Neurology',
  'Oncology',
  'Pediatrics',
  'Internal Medicine',
  'Family Medicine',
  'Emergency Medicine',
  'Surgery',
  'Obstetrics & Gynecology',
  'Psychiatry',
  'Dermatology',
  'Endocrinology',
  'Gastroenterology',
  'Pulmonology',
  'Rheumatology',
  'Urology',
  'Radiology',
  'Anesthesiology',
  'Pathology',
  'Physical Medicine & Rehabilitation',
  'Allergy & Immunology',
  'Infectious Disease',
  'Nephrology',
  'Hematology',
  'Pain Management',
  'Other'
];

// Add subspecialties
const subspecialties = [
  // Cardiology subspecialties
  'Interventional Cardiology',
  'Electrophysiology',
  'Heart Failure',
  'Nuclear Cardiology',
  'Cardiac Surgery',
  // Orthopedics subspecialties
  'Sports Medicine',
  'Spine Surgery',
  'Joint Replacement',
  'Hand Surgery',
  'Foot & Ankle Surgery',
  'Trauma Surgery',
  // Neurology subspecialties
  'Stroke/Cerebrovascular',
  'Epilepsy',
  'Movement Disorders',
  'Neuromuscular Medicine',
  'Pediatric Neurology',
  // Oncology subspecialties
  'Medical Oncology',
  'Radiation Oncology',
  'Surgical Oncology',
  'Hematology-Oncology',
  'Gynecologic Oncology',
  // Surgery subspecialties
  'General Surgery',
  'Vascular Surgery',
  'Plastic Surgery',
  'Thoracic Surgery',
  'Neurosurgery',
  'Colorectal Surgery',
  // Pediatrics subspecialties
  'Pediatric Cardiology',
  'Pediatric Endocrinology',
  'Pediatric Gastroenterology',
  'Neonatology',
  'Pediatric Surgery',
  // Radiology subspecialties
  'Diagnostic Radiology',
  'Interventional Radiology',
  'Nuclear Medicine',
  'Neuroradiology',
  'Mammography',
  // Internal Medicine subspecialties
  'Critical Care Medicine',
  'Hospitalist Medicine',
  'Geriatric Medicine',
  'Sleep Medicine',
  'Addiction Medicine',
  // Other common subspecialties
  'Maternal-Fetal Medicine',
  'Reproductive Endocrinology',
  'Pediatric Intensive Care',
  'Emergency Medicine - Pediatric',
  'Forensic Psychiatry',
  'Child & Adolescent Psychiatry',
  'Other'
];

// Healthcare Organizations
const healthcareOrganizations = [
  'Orlando Health',
  'AdventHealth',
  'Cleveland Clinic Florida',
  'Mayo Clinic Florida',
  'Johns Hopkins All Children\'s Hospital',
  'Tampa General Hospital',
  'Jackson Health System',
  'Baptist Health South Florida',
  'Memorial Healthcare System',
  'Broward Health',
  'Lee Health',
  'Sarasota Memorial Health Care System',
  'BayCare Health System',
  'Halifax Health',
  'Lakeland Regional Health',
  'Palm Beach Health Network',
  'HCA Healthcare',
  'Tenet Healthcare',
  'Community Health Systems',
  'Universal Health Services',
  'Kaiser Permanente',
  'Aetna Better Health',
  'Humana',
  'WellCare',
  'Molina Healthcare',
  'Other'
];

// Affiliated Hospitals
const affiliatedHospitals = [
  // Orlando/Central Florida Hospitals
  'Orlando Regional Medical Center',
  'Dr. P. Phillips Hospital',
  'Arnold Palmer Hospital for Children',
  'Winnie Palmer Hospital for Women & Babies',
  'Health Central Hospital',
  'AdventHealth Orlando',
  'AdventHealth Celebration',
  'AdventHealth East Orlando',
  'AdventHealth Winter Park',
  'Florida Hospital Altamonte',
  'Winter Park Memorial Hospital',
  'AdventHealth Apopka',
  'AdventHealth Waterman',
  // Tampa Bay Area Hospitals
  'Tampa General Hospital',
  'AdventHealth Tampa',
  'St. Joseph\'s Hospital',
  'James A. Haley Veterans\' Hospital',
  'Moffitt Cancer Center',
  'BayCare St. Joseph\'s Hospital',
  'Tampa Community Hospital',
  'University of South Florida Health',
  'AdventHealth Wesley Chapel',
  'Brandon Regional Hospital',
  'St. Petersburg General Hospital',
  // Miami/South Florida Hospitals
  'Jackson Memorial Hospital',
  'Baptist Hospital of Miami',
  'Mount Sinai Medical Center',
  'Aventura Hospital and Medical Center',
  'Memorial Regional Hospital',
  'Joe DiMaggio Children\'s Hospital',
  'Cleveland Clinic Florida',
  'Holy Cross Hospital',
  'Broward Health Medical Center',
  'University of Miami Hospital',
  'Nicklaus Children\'s Hospital',
  // Jacksonville/North Florida Hospitals
  'Mayo Clinic Jacksonville',
  'UF Health Jacksonville',
  'Baptist Medical Center Jacksonville',
  'Memorial Hospital Jacksonville',
  'St. Vincent\'s Medical Center',
  'Wolfson Children\'s Hospital',
  'Orange Park Medical Center',
  // Other Major Florida Hospitals
  'Shands Hospital at University of Florida',
  'Halifax Health Medical Center',
  'AdventHealth Daytona Beach',
  'Lakeland Regional Health Medical Center',
  'Lee Health - Lee Memorial Hospital',
  'NCH Baker Hospital Downtown',
  'Sarasota Memorial Hospital',
  'Other'
];

// Facility types that should show specialty options
const facilityTypesWithSpecialties = [
  'Outpatient Clinic',
  'Ambulatory Surgical Center',
  'General Hospital',
  'Specialty Hospital',
  'Behavioural Health Center',
  'Psychiatric Treatment Center'
];

interface MapFiltersProps {
  onApplyFilters: (filters: any) => void;
}

const MapFilters: React.FC<MapFiltersProps> = ({ onApplyFilters }) => {
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<string>('1month');
  const [showPredictions, setShowPredictions] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [customTimeRange, setCustomTimeRange] = useState<[number, number]>([0, 12]);
  const [patientVolumeRange, setPatientVolumeRange] = useState<[number, number]>([0, 1000]);
  const [revenueRange, setRevenueRange] = useState<[number, number]>([0, 1000000]);
  const [fteProvidersRange, setFteProvidersRange] = useState<[number, number]>([0, 100]);
  const [leaseExpirationRange, setLeaseExpirationRange] = useState<[number, number]>([0, 60]); // months from now
  
  // Provider filter states
  const [providerFullName, setProviderFullName] = useState<string>('');
  const [selectedProviderTypes, setSelectedProviderTypes] = useState<string[]>([]);
  const [selectedProviderSpecialties, setSelectedProviderSpecialties] = useState<string[]>([]);
  const [selectedProviderSubspecialties, setSelectedProviderSubspecialties] = useState<string[]>([]);
  const [providerCareScoreRange, setProviderCareScoreRange] = useState<[number, number]>([0, 100]);
  const [selectedPrimaryHealthcareOrgs, setSelectedPrimaryHealthcareOrgs] = useState<string[]>([]);
  const [primaryPracticeLocation, setPrimaryPracticeLocation] = useState<string>('');
  const [secondaryPracticeLocation, setSecondaryPracticeLocation] = useState<string>('');
  const [selectedAffiliatedHospitals, setSelectedAffiliatedHospitals] = useState<string[]>([]);
  const [providerStarRatingRange, setProviderStarRatingRange] = useState<[number, number]>([1, 5]);
  const [patientPanelSizeRange, setPatientPanelSizeRange] = useState<[number, number]>([0, 5000]);
  const [estimatedProductionValueRange, setEstimatedProductionValueRange] = useState<[number, number]>([0, 2000000]);
  const [scheduleUtilizationRange, setScheduleUtilizationRange] = useState<[number, number]>([0, 100]);
  const [providerProductivityPercentileRange, setProviderProductivityPercentileRange] = useState<[number, number]>([0, 100]);
  
  const [locationFilters, setLocationFilters] = useState({
    address: '',
    city: '',
    commonName: '',
    zip: '',
    county: '',
    state: '',
    locationId: '',
    squareFootage: '',
    specialty: '',
    serviceLine: ''
  });

  // Check if any selected facility type requires specialty selection
  const shouldShowSpecialties = activeFilters.some(filter => 
    facilityTypesWithSpecialties.includes(filter)
  );

  const handleAddFilter = (filter: string) => {
    if (!activeFilters.includes(filter)) {
      setActiveFilters([...activeFilters, filter]);
    }
  };

  const handleRemoveFilter = (filter: string) => {
    setActiveFilters(activeFilters.filter(f => f !== filter));
  };

  const handleLocationChange = (field: string, value: string) => {
    setLocationFilters({ ...locationFilters, [field]: value });
    if (value && !activeFilters.includes(field)) {
      handleAddFilter(field);
    } else if (!value && activeFilters.includes(field)) {
      handleRemoveFilter(field);
    }
  };

  // Apply filters whenever provider filters change
  useEffect(() => {
    const currentFilters = {
      activeFilters,
      selectedSpecialties,
      timeRange,
      showPredictions,
      patientVolumeRange,
      revenueRange,
      fteProvidersRange,
      leaseExpirationRange,
      locationFilters,
      providerFullName,
      selectedProviderTypes,
      selectedProviderSpecialties,
      selectedProviderSubspecialties,
      providerCareScoreRange,
      selectedPrimaryHealthcareOrgs,
      primaryPracticeLocation,
      secondaryPracticeLocation,
      selectedAffiliatedHospitals,
      providerStarRatingRange,
      patientPanelSizeRange,
      estimatedProductionValueRange,
      scheduleUtilizationRange,
      providerProductivityPercentileRange
    };
    onApplyFilters(currentFilters);
  }, [
    activeFilters,
    selectedSpecialties,
    timeRange,
    showPredictions,
    patientVolumeRange,
    revenueRange,
    fteProvidersRange,
    leaseExpirationRange,
    locationFilters,
    providerFullName,
    selectedProviderTypes,
    selectedProviderSpecialties,
    selectedProviderSubspecialties,
    providerCareScoreRange,
    selectedPrimaryHealthcareOrgs,
    primaryPracticeLocation,
    secondaryPracticeLocation,
    selectedAffiliatedHospitals,
    providerStarRatingRange,
    patientPanelSizeRange,
    estimatedProductionValueRange,
    scheduleUtilizationRange,
    providerProductivityPercentileRange,
    onApplyFilters
  ]);

  const getScoreColor = (score: number) => {
    if (score >= 85) return '#4caf50';
    if (score >= 70) return '#ff9800';
    return '#f44336';
  };

  const handleTimeRangeChange = (event: Event, newValue: number | number[]) => {
    setCustomTimeRange(newValue as [number, number]);
  };

  const handleVolumeRangeChange = (event: Event, newValue: number | number[]) => {
    setPatientVolumeRange(newValue as [number, number]);
  };

  const handleRevenueRangeChange = (event: Event, newValue: number | number[]) => {
    setRevenueRange(newValue as [number, number]);
  };

  const handleFteProvidersRangeChange = (event: Event, newValue: number | number[]) => {
    setFteProvidersRange(newValue as [number, number]);
  };

  const handleLeaseExpirationRangeChange = (event: Event, newValue: number | number[]) => {
    setLeaseExpirationRange(newValue as [number, number]);
  };

  const handleProviderFullNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProviderFullName(event.target.value);
  };

  const handleProviderTypesChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedProviderTypes(event.target.value as string[]);
  };

  const handleProviderSpecialtiesChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedProviderSpecialties(event.target.value as string[]);
  };

  const handleProviderSubspecialtiesChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedProviderSubspecialties(event.target.value as string[]);
  };

  const handleProviderCareScoreRangeChange = (event: Event, newValue: number | number[]) => {
    setProviderCareScoreRange(newValue as [number, number]);
  };

  const handlePrimaryHealthcareOrgsChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedPrimaryHealthcareOrgs(event.target.value as string[]);
  };

  const handlePrimaryPracticeLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrimaryPracticeLocation(event.target.value);
  };

  const handleSecondaryPracticeLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSecondaryPracticeLocation(event.target.value);
  };

  const handleAffiliatedHospitalsChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedAffiliatedHospitals(event.target.value as string[]);
  };

  const handleProviderStarRatingRangeChange = (event: Event, newValue: number | number[]) => {
    setProviderStarRatingRange(newValue as [number, number]);
  };

  const handlePatientPanelSizeRangeChange = (event: Event, newValue: number | number[]) => {
    setPatientPanelSizeRange(newValue as [number, number]);
  };

  const handleEstimatedProductionValueRangeChange = (event: Event, newValue: number | number[]) => {
    setEstimatedProductionValueRange(newValue as [number, number]);
  };

  const handleScheduleUtilizationRangeChange = (event: Event, newValue: number | number[]) => {
    setScheduleUtilizationRange(newValue as [number, number]);
  };

  const handleProviderProductivityPercentileRangeChange = (event: Event, newValue: number | number[]) => {
    setProviderProductivityPercentileRange(newValue as [number, number]);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <Box 
      sx={{ 
        width: 320,
        height: '100%',
        bgcolor: 'background.default',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Card 
        elevation={0} 
        sx={{ 
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <CardContent sx={{ py: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontSize: '1.1rem',
              fontWeight: 500
            }}
          >
            <FilterIcon fontSize="small" /> Network Filters
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto'
      }}>
        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Active Filters
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {activeFilters.map(filter => (
                <Chip
                  key={filter}
                  label={filter}
                  onDelete={() => handleRemoveFilter(filter)}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Facility Type Filter */}
        <Accordion defaultExpanded>
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 56,
              '& .MuiAccordionSummary-content': {
                m: 0
              }
            }}
          >
            <Typography 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontWeight: 500
              }}
            >
              <HospitalIcon fontSize="small" color="primary" />
              Facility Type
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, pt: 0 }}>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Facility Types</InputLabel>
                <Select
                  multiple
                  value={activeFilters.filter(f => facilityTypes.includes(f))}
                  onChange={(e) => {
                    const values = e.target.value as string[];
                    setActiveFilters([
                      ...activeFilters.filter(f => !facilityTypes.includes(f)),
                      ...values
                    ]);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {facilityTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {shouldShowSpecialties && (
                <FormControl fullWidth size="small">
                  <InputLabel>Specialty / Subspecialty</InputLabel>
                  <Select
                    multiple
                    value={selectedSpecialties}
                    onChange={(e) => {
                      const values = e.target.value as string[];
                      setSelectedSpecialties(values);
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {specialties.map((specialty) => (
                      <MenuItem key={specialty} value={specialty}>
                        {specialty}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Enhanced Location Filter */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
              <LocationIcon fontSize="small" color="primary" />
              Location
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, pt: 0 }}>
            <Stack spacing={2}>
              <TextField
                label="Commonly Known Name"
                size="small"
                value={locationFilters.commonName}
                onChange={(e) => handleLocationChange('commonName', e.target.value)}
              />
              <TextField
                label="Street Address"
                size="small"
                value={locationFilters.address}
                onChange={(e) => handleLocationChange('address', e.target.value)}
              />
              <TextField
                label="City"
                size="small"
                value={locationFilters.city}
                onChange={(e) => handleLocationChange('city', e.target.value)}
              />
              <TextField
                label="ZIP Code"
                size="small"
                value={locationFilters.zip}
                onChange={(e) => handleLocationChange('zip', e.target.value)}
              />
              <TextField
                label="County"
                size="small"
                value={locationFilters.county}
                onChange={(e) => handleLocationChange('county', e.target.value)}
              />
              <TextField
                label="State"
                size="small"
                value={locationFilters.state}
                onChange={(e) => handleLocationChange('state', e.target.value)}
              />
              <TextField
                label="Unique Location ID"
                size="small"
                value={locationFilters.locationId}
                onChange={(e) => handleLocationChange('locationId', e.target.value)}
              />
              <TextField
                label="Square Footage"
                size="small"
                type="number"
                value={locationFilters.squareFootage}
                onChange={(e) => handleLocationChange('squareFootage', e.target.value)}
              />
              <TextField
                label="Specialty / Subspecialty"
                size="small"
                value={locationFilters.specialty}
                onChange={(e) => handleLocationChange('specialty', e.target.value)}
              />
              <TextField
                label="Service Line"
                size="small"
                value={locationFilters.serviceLine}
                onChange={(e) => handleLocationChange('serviceLine', e.target.value)}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Facility Filters */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
              <HospitalIcon fontSize="small" color="primary" />
              Facility Filters
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, pt: 0 }}>
            <Stack spacing={2}>
              {/* FTE Providers Range Slider */}
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  # of FTE Providers: {fteProvidersRange[0]} - {fteProvidersRange[1]}
                </Typography>
                <Slider
                  value={fteProvidersRange}
                  onChange={handleFteProvidersRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  size="small"
                  sx={{ color: 'primary.main' }}
                />
              </Box>

              {/* Lease Expiration Range Slider */}
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Lease Expiration (months from now): {leaseExpirationRange[0]} - {leaseExpirationRange[1]}
                </Typography>
                <Slider
                  value={leaseExpirationRange}
                  onChange={handleLeaseExpirationRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={60}
                  size="small"
                  sx={{ color: 'primary.main' }}
                />
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Enhanced Patient Volume Filter */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
              <AnalyticsIcon fontSize="small" color="primary" />
              Patient Volume
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, pt: 0 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Volume Range</Typography>
                <Slider
                  value={patientVolumeRange}
                  onChange={handleVolumeRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={1000}
                />
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>Time Frame</Typography>
                <FormControl fullWidth size="small">
                  <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                    <MenuItem value="1month">1 Month</MenuItem>
                    <MenuItem value="6months">6 Months</MenuItem>
                    <MenuItem value="ytd">Year to Date</MenuItem>
                    <MenuItem value="1year">1 Year</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>
                {timeRange === 'custom' && (
                  <Slider
                    value={customTimeRange}
                    onChange={handleTimeRangeChange}
                    valueLabelDisplay="auto"
                    min={0}
                    max={12}
                    marks
                    valueLabelFormat={(value) => `${value}mo`}
                  />
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Visualization Options</Typography>
                <Stack direction="row" spacing={1}>
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="Heatmap"
                  />
                  <Tooltip title="Play/Pause Animation">
                    <IconButton size="small" onClick={togglePlayPause}>
                      {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Predictions</Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="6 Month Prediction"
                  />
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="1 Year Prediction"
                  />
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="5 Year Prediction"
                  />
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Enhanced Performance Metrics Filter */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
              <MetricsIcon fontSize="small" color="primary" />
              Performance Metrics
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, pt: 0 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Revenue Range ($)</Typography>
                <Slider
                  value={revenueRange}
                  onChange={handleRevenueRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={1000000}
                  valueLabelFormat={(value) => `$${value.toLocaleString()}`}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Time Frame</Typography>
                <FormControl fullWidth size="small">
                  <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                    <MenuItem value="1month">1 Month</MenuItem>
                    <MenuItem value="6months">6 Months</MenuItem>
                    <MenuItem value="ytd">Year to Date</MenuItem>
                    <MenuItem value="1year">1 Year</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Visualization</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="Heatmap"
                  />
                  <Tooltip title="Play/Pause Animation">
                    <IconButton size="small" onClick={togglePlayPause}>
                      {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Timeline">
                    <IconButton size="small">
                      <TimelineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Predictions</Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="6 Month Prediction"
                  />
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="1 Year Prediction"
                  />
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="5 Year Prediction"
                  />
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Enhanced Care Score Filter */}
        <Accordion>
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 56,
              '& .MuiAccordionSummary-content': {
                m: 0
              }
            }}
          >
            <Typography 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontWeight: 500
              }}
            >
              <CareScoreIcon fontSize="small" color="primary" />
              Care Score
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, pt: 0 }}>
            <Stack spacing={2}>
              <FormControlLabel
                control={<Switch defaultChecked size="small" />}
                label={
                  <Typography variant="body2">Show Care Score Overlay</Typography>
                }
              />
              <FormControlLabel
                control={<Switch size="small" />}
                label={
                  <Typography variant="body2">Show Predictions</Typography>
                }
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Add Providers Filter */}
        <Accordion>
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 56,
              '& .MuiAccordionSummary-content': {
                m: 0
              }
            }}
          >
            <Typography 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontWeight: 500
              }}
            >
              <ProviderIcon fontSize="small" color="primary" />
              Providers
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, pt: 0 }}>
            <Stack spacing={2}>
              {/* Provider Full Name Filter */}
              <FormControl fullWidth size="small">
                <TextField
                  label="Provider Full Name"
                  value={providerFullName}
                  onChange={handleProviderFullNameChange}
                  placeholder="Search by provider name"
                  size="small"
                />
              </FormControl>

              {/* Provider Type Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Provider Type</InputLabel>
                <Select
                  multiple
                  value={selectedProviderTypes}
                  onChange={handleProviderTypesChange}
                  label="Provider Type"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip 
                          key={value} 
                          label={value.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} 
                          size="small" 
                        />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="physician">Physician</MenuItem>
                  <MenuItem value="nurse_practitioner">Nurse Practitioner</MenuItem>
                  <MenuItem value="physician_assistant">Physician Assistant</MenuItem>
                  <MenuItem value="specialist">Specialist</MenuItem>
                  <MenuItem value="surgeon">Surgeon</MenuItem>
                  <MenuItem value="radiologist">Radiologist</MenuItem>
                  <MenuItem value="anesthesiologist">Anesthesiologist</MenuItem>
                  <MenuItem value="pathologist">Pathologist</MenuItem>
                  <MenuItem value="hospitalist">Hospitalist</MenuItem>
                  <MenuItem value="emergency_medicine">Emergency Medicine</MenuItem>
                  <MenuItem value="therapist">Therapist</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              {/* Provider Specialty Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Specialty(s)</InputLabel>
                <Select
                  multiple
                  value={selectedProviderSpecialties}
                  onChange={handleProviderSpecialtiesChange}
                  label="Specialty(s)"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {specialties.map((specialty) => (
                    <MenuItem key={specialty} value={specialty}>
                      {specialty}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Provider Subspecialty Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Subspecialty(s)</InputLabel>
                <Select
                  multiple
                  value={selectedProviderSubspecialties}
                  onChange={handleProviderSubspecialtiesChange}
                  label="Subspecialty(s)"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {subspecialties.map((subspecialty) => (
                    <MenuItem key={subspecialty} value={subspecialty}>
                      {subspecialty}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Provider Care Score Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 1 }}>
                  Provider Care Score
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Score Range: {providerCareScoreRange[0]} - {providerCareScoreRange[1]}
                </Typography>
                <Slider
                  value={providerCareScoreRange}
                  onChange={handleProviderCareScoreRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  size="small"
                  sx={{ color: 'primary.main' }}
                />
              </Box>

              {/* Primary Healthcare Organization */}
              <FormControl fullWidth size="small">
                <InputLabel>Primary Healthcare Organization</InputLabel>
                <Select
                  multiple
                  value={selectedPrimaryHealthcareOrgs}
                  onChange={handlePrimaryHealthcareOrgsChange}
                  label="Primary Healthcare Organization"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {healthcareOrganizations.map((org) => (
                    <MenuItem key={org} value={org}>
                      {org}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Primary Practice Location */}
              <FormControl fullWidth size="small">
                <TextField
                  label="Primary Practice Location"
                  value={primaryPracticeLocation}
                  onChange={handlePrimaryPracticeLocationChange}
                  placeholder="Enter primary practice address or location"
                  size="small"
                />
              </FormControl>

              {/* Secondary Practice Location */}
              <FormControl fullWidth size="small">
                <TextField
                  label="Secondary Practice Location"
                  value={secondaryPracticeLocation}
                  onChange={handleSecondaryPracticeLocationChange}
                  placeholder="Enter secondary practice address or location"
                  size="small"
                />
              </FormControl>

              {/* Affiliated Hospitals */}
              <FormControl fullWidth size="small">
                <InputLabel>Affiliated Hospitals</InputLabel>
                <Select
                  multiple
                  value={selectedAffiliatedHospitals}
                  onChange={handleAffiliatedHospitalsChange}
                  label="Affiliated Hospitals"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {affiliatedHospitals.map((hospital) => (
                    <MenuItem key={hospital} value={hospital}>
                      {hospital}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Provider Star Rating Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 1 }}>
                  Healthgrades/WebMD Star Rating
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Rating Range: {providerStarRatingRange[0]} - {providerStarRatingRange[1]} stars
                </Typography>
                <Slider
                  value={providerStarRatingRange}
                  onChange={handleProviderStarRatingRangeChange}
                  valueLabelDisplay="auto"
                  min={1}
                  max={5}
                  step={0.5}
                  marks={[
                    { value: 1, label: '1★' },
                    { value: 2, label: '2★' },
                    { value: 3, label: '3★' },
                    { value: 4, label: '4★' },
                    { value: 5, label: '5★' }
                  ]}
                  size="small"
                  sx={{ color: 'warning.main' }}
                  valueLabelFormat={(value) => `${value}★`}
                />
              </Box>

              {/* Patient Panel Size Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 1 }}>
                  Patient Panel Size
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Panel Size: {patientPanelSizeRange[0].toLocaleString()} - {patientPanelSizeRange[1].toLocaleString()} patients
                </Typography>
                <Slider
                  value={patientPanelSizeRange}
                  onChange={handlePatientPanelSizeRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={5000}
                  step={50}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 1000, label: '1K' },
                    { value: 2500, label: '2.5K' },
                    { value: 5000, label: '5K' }
                  ]}
                  size="small"
                  sx={{ color: 'info.main' }}
                  valueLabelFormat={(value) => value.toLocaleString()}
                />
              </Box>

              {/* Estimated Production Value Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 1 }}>
                  Estimated Production Value
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Production Value: ${estimatedProductionValueRange[0].toLocaleString()} - ${estimatedProductionValueRange[1].toLocaleString()}
                </Typography>
                <Slider
                  value={estimatedProductionValueRange}
                  onChange={handleEstimatedProductionValueRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={2000000}
                  step={25000}
                  marks={[
                    { value: 0, label: '$0' },
                    { value: 500000, label: '$500K' },
                    { value: 1000000, label: '$1M' },
                    { value: 2000000, label: '$2M' }
                  ]}
                  size="small"
                  sx={{ color: 'success.main' }}
                  valueLabelFormat={(value) => `$${(value / 1000).toFixed(0)}K`}
                />
              </Box>

              {/* Schedule Utilization % Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 1 }}>
                  Schedule Utilization %
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Utilization: {scheduleUtilizationRange[0]}% - {scheduleUtilizationRange[1]}%
                </Typography>
                <Slider
                  value={scheduleUtilizationRange}
                  onChange={handleScheduleUtilizationRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 25, label: '25%' },
                    { value: 50, label: '50%' },
                    { value: 75, label: '75%' },
                    { value: 100, label: '100%' }
                  ]}
                  size="small"
                  sx={{ color: 'warning.main' }}
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Box>

              {/* Provider Productivity Percentile Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 1 }}>
                  Provider Productivity Percentile
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Percentile: {providerProductivityPercentileRange[0]} - {providerProductivityPercentileRange[1]}
                </Typography>
                <Slider
                  value={providerProductivityPercentileRange}
                  onChange={handleProviderProductivityPercentileRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: '0th' },
                    { value: 25, label: '25th' },
                    { value: 50, label: '50th' },
                    { value: 75, label: '75th' },
                    { value: 100, label: '100th' }
                  ]}
                  size="small"
                  sx={{ color: 'secondary.main' }}
                  valueLabelFormat={(value) => `${value}th`}
                />
              </Box>

              {/* Show selected provider filters as chips */}
              {(selectedProviderTypes.length > 0 || 
                selectedProviderSpecialties.length > 0 || 
                selectedProviderSubspecialties.length > 0 ||
                selectedPrimaryHealthcareOrgs.length > 0 ||
                primaryPracticeLocation ||
                secondaryPracticeLocation ||
                selectedAffiliatedHospitals.length > 0 ||
                (patientPanelSizeRange[0] !== 0 || patientPanelSizeRange[1] !== 5000) ||
                (estimatedProductionValueRange[0] !== 0 || estimatedProductionValueRange[1] !== 2000000) ||
                (scheduleUtilizationRange[0] !== 0 || scheduleUtilizationRange[1] !== 100) ||
                (providerProductivityPercentileRange[0] !== 0 || providerProductivityPercentileRange[1] !== 100)) && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Selected Filters
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                      onClick={() => {
                        setSelectedProviderTypes([]);
                        setSelectedProviderSpecialties([]);
                        setSelectedProviderSubspecialties([]);
                        setSelectedPrimaryHealthcareOrgs([]);
                        setPrimaryPracticeLocation('');
                        setSecondaryPracticeLocation('');
                        setProviderCareScoreRange([0, 100]);
                        setSelectedAffiliatedHospitals([]);
                        setProviderStarRatingRange([1, 5]);
                        setPatientPanelSizeRange([0, 5000]);
                        setEstimatedProductionValueRange([0, 2000000]);
                        setScheduleUtilizationRange([0, 100]);
                        setProviderProductivityPercentileRange([0, 100]);
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {/* Provider Type Chips */}
                    {selectedProviderTypes.map((type) => (
                      <Chip
                        key={`type-${type}`}
                        label={`Type: ${type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={() => {
                          setSelectedProviderTypes(selectedProviderTypes.filter(t => t !== type));
                        }}
                      />
                    ))}
                    {/* Provider Specialty Chips */}
                    {selectedProviderSpecialties.map((specialty) => (
                      <Chip
                        key={`specialty-${specialty}`}
                        label={`Specialty: ${specialty}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onDelete={() => {
                          setSelectedProviderSpecialties(selectedProviderSpecialties.filter(s => s !== specialty));
                        }}
                      />
                    ))}
                    {/* Provider Subspecialty Chips */}
                    {selectedProviderSubspecialties.map((subspecialty) => (
                      <Chip
                        key={`subspecialty-${subspecialty}`}
                        label={`Subspecialty: ${subspecialty}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        onDelete={() => {
                          setSelectedProviderSubspecialties(selectedProviderSubspecialties.filter(s => s !== subspecialty));
                        }}
                      />
                    ))}
                    {/* Healthcare Organization Chips */}
                    {selectedPrimaryHealthcareOrgs.map((org) => (
                      <Chip
                        key={`org-${org}`}
                        label={`Organization: ${org}`}
                        size="small"
                        color="success"
                        variant="outlined"
                        onDelete={() => {
                          setSelectedPrimaryHealthcareOrgs(selectedPrimaryHealthcareOrgs.filter(o => o !== org));
                        }}
                      />
                    ))}
                    {/* Primary Practice Location Chip */}
                    {primaryPracticeLocation && (
                      <Chip
                        key="primary-location"
                        label={`Primary Location: ${primaryPracticeLocation}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        onDelete={() => setPrimaryPracticeLocation('')}
                      />
                    )}
                    {/* Secondary Practice Location Chip */}
                    {secondaryPracticeLocation && (
                      <Chip
                        key="secondary-location"
                        label={`Secondary Location: ${secondaryPracticeLocation}`}
                        size="small"
                        color="default"
                        variant="outlined"
                        onDelete={() => setSecondaryPracticeLocation('')}
                      />
                    )}
                    {/* Affiliated Hospitals Chips */}
                    {selectedAffiliatedHospitals.map((hospital) => (
                      <Chip
                        key={`hospital-${hospital}`}
                        label={`Hospital: ${hospital}`}
                        size="small"
                        color="error"
                        variant="outlined"
                        onDelete={() => {
                          setSelectedAffiliatedHospitals(selectedAffiliatedHospitals.filter(h => h !== hospital));
                        }}
                      />
                    ))}
                    {/* Star Rating Chip - only show if not default range */}
                    {(providerStarRatingRange[0] !== 1 || providerStarRatingRange[1] !== 5) && (
                      <Chip
                        key="star-rating"
                        label={`Rating: ${providerStarRatingRange[0]}★ - ${providerStarRatingRange[1]}★`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        onDelete={() => setProviderStarRatingRange([1, 5])}
                      />
                    )}
                    {/* Patient Panel Size Chip - only show if not default range */}
                    {(patientPanelSizeRange[0] !== 0 || patientPanelSizeRange[1] !== 5000) && (
                      <Chip
                        key="panel-size"
                        label={`Panel Size: ${patientPanelSizeRange[0].toLocaleString()} - ${patientPanelSizeRange[1].toLocaleString()}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        onDelete={() => setPatientPanelSizeRange([0, 5000])}
                      />
                    )}
                    {/* Estimated Production Value Chip - only show if not default range */}
                    {(estimatedProductionValueRange[0] !== 0 || estimatedProductionValueRange[1] !== 2000000) && (
                      <Chip
                        key="production-value"
                        label={`Production: $${(estimatedProductionValueRange[0] / 1000).toFixed(0)}K - $${(estimatedProductionValueRange[1] / 1000).toFixed(0)}K`}
                        size="small"
                        color="success"
                        variant="outlined"
                        onDelete={() => setEstimatedProductionValueRange([0, 2000000])}
                      />
                    )}
                    {/* Schedule Utilization Chip - only show if not default range */}
                    {(scheduleUtilizationRange[0] !== 0 || scheduleUtilizationRange[1] !== 100) && (
                      <Chip
                        key="schedule-utilization"
                        label={`Utilization: ${scheduleUtilizationRange[0]}% - ${scheduleUtilizationRange[1]}%`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        onDelete={() => setScheduleUtilizationRange([0, 100])}
                      />
                    )}
                    {/* Provider Productivity Percentile Chip - only show if not default range */}
                    {(providerProductivityPercentileRange[0] !== 0 || providerProductivityPercentileRange[1] !== 100) && (
                      <Chip
                        key="productivity-percentile"
                        label={`Productivity: ${providerProductivityPercentileRange[0]}th - ${providerProductivityPercentileRange[1]}th percentile`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onDelete={() => setProviderProductivityPercentileRange([0, 100])}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Enhanced Patient Cohorts Filter */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
              <CohortsIcon fontSize="small" color="primary" />
              Patient Cohorts
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, pt: 0 }}>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Cohort</InputLabel>
                <Select>
                  <MenuItem value="cohort1">High-Risk Patients</MenuItem>
                  <MenuItem value="cohort2">Chronic Care</MenuItem>
                  <MenuItem value="cohort3">Preventive Care</MenuItem>
                </Select>
              </FormControl>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Cohort Details</Typography>
                <Stack spacing={1}>
                  <Link href="#" underline="hover">View Full Cohort Details</Link>
                  <Typography variant="body2" color="text.secondary">
                    1,234 Patients • Last Updated: Today
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Visualization</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="Patient Journeys"
                  />
                  <Tooltip title="View Routes">
                    <IconButton size="small">
                      <RouteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Predictions</Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="6 Month Prediction"
                  />
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="1 Year Prediction"
                  />
                  <FormControlLabel
                    control={<Switch size="small" />}
                    label="5 Year Prediction"
                  />
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default MapFilters; 